import ReactRouterPrompt from "react-router-prompt"
import { useCallback, useEffect, useRef } from "react"
import { WarningModal } from "./WarningModal"
import { useCMSReady } from "../../../domain/CMS/useCMSReady"
import type { PromptRenderProps } from "react-router-prompt"

export interface UnsavedWarningProps {
  when: boolean
  additionalOnConfirm?: () => void
  onSecondary?: () => void
  text?: {
    title?: string
    confirmButton?: string
    secondaryButton?: string
  }
  body?: string | JSX.Element
}

const KEY_NAME_ESC = "Escape",
  KEY_EVENT_TYPE = "keydown"

export const UnsavedWarning: React.FC<UnsavedWarningProps> = ({
  when,
  additionalOnConfirm,
  onSecondary,
  text,
  body,
}) => {
  const { cms: cmsContent } = useCMSReady(["common"])
  const commonText = cmsContent.common
  const cancelRef = useRef<(() => void) | null>(null)
  const handleEscKey = useCallback(
    (event: KeyboardEvent) => {
      if (!cancelRef.current) return

      if (event.key === KEY_NAME_ESC) {
        cancelRef.current()
      }
    },
    [cancelRef],
  )

  useEffect(() => {
    document.addEventListener(KEY_EVENT_TYPE, handleEscKey, false)

    return () => {
      document.removeEventListener(KEY_EVENT_TYPE, handleEscKey, false)
    }
  }, [cancelRef, handleEscKey])

  // TODO: Discuss skeletons or spinners soon.
  if (!commonText) return null

  return (
    <ReactRouterPrompt key={`${location.pathname}-${when}`} when={when}>
      {({ isActive, onConfirm, onCancel }: PromptRenderProps) => {
        cancelRef.current = onCancel
        return !isActive ? null : (
          <WarningModal
            isOpen
            onConfirm={() => {
              additionalOnConfirm?.()
              onConfirm()
            }}
            onCancel={onCancel}
            onSecondary={onSecondary}
            text={{
              title: text?.title,
              confirmButton: text?.confirmButton ?? commonText?.leavePage,
              secondaryButton: text?.secondaryButton,
              dialogAriaLabel: commonText?.unsavedWarningAriaLabel ?? "",
            }}
          >
            {body || commonText?.unsavedChangesMessage}
          </WarningModal>
        )
      }}
    </ReactRouterPrompt>
  )
}
